import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, DollarSign, Calendar, ExternalLink, Trash2, AlertCircle, ChevronLeft, ChevronRight, Search, RefreshCw, Lock, LockOpen, Globe, Layers, Users, Truck } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { PayeeFeesPanel } from '../components/PayeeFeesPanel';
import PayTransfersPanel from '../components/PayTransfersPanel';
import StaffSalaryPanel from '../components/StaffSalaryPanel';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../lib/telemetry';
import { PayrollStatusBadge } from '../components/PayrollSendControls';
import { PayrollAttentionBanner } from '../components/PayrollAttentionBanner';
import { FeeRulePicker, feeRuleToBody, describeFeeRule, describeOverride, money as feeMoney } from '../components/AdminFeeControls';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BROWSER_TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; } })();
const TZ_OPTIONS = Array.from(new Set([BROWSER_TZ, 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC']));


const ExcludedLoadsBadge = ({ payroll }) => {
  const [open, setOpen] = useState(false);
  const items = payroll.excluded_loads || [];
  if (!items.length) return null;
  return (
    <span className="relative inline-block ml-1">
      <button type="button" data-testid={`excluded-loads-${payroll.id}`} onClick={() => setOpen((v) => !v)}
        title="Loads in this period that were NOT paid — click for reasons"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 hover:bg-amber-200">
        <AlertCircle className="w-3 h-3" /> {items.length} not paid
      </button>
      {open && (
        <div data-testid={`excluded-loads-panel-${payroll.id}`} className="absolute z-30 mt-1 left-0 w-[420px] max-h-72 overflow-y-auto bg-white border border-amber-300 rounded-md shadow-xl p-3 text-left" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-2">Loads not included in this run</p>
          <ul className="space-y-2">
            {items.map((l) => (
              <li key={l.load_id} className="text-xs border-b border-slate-100 pb-1.5">
                <div className="font-semibold text-slate-900">{l.load_ref || l.load_id.slice(-6)} · {l.driver_name || 'no driver'}{l.load_rate ? ` · $${Number(l.load_rate).toLocaleString()}` : ''}</div>
                <div className="text-slate-500">{l.route}{l.delivery_date ? ` · delivery ${l.delivery_date}` : ''}</div>
                <div className="text-amber-800 mt-0.5">{l.reason}</div>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-slate-500 mt-2">Fix the cause, then use Regenerate on this payroll (requires an unlocked run).</p>
        </div>
      )}
    </span>
  );
};

const Payroll = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [reimb, setReimb] = useState(null);

  // Business details that appear on every paystub PDF header. Same checks
  // we run on the Invoices page so admins are nudged in both surfaces.
  const missingBusinessFields = useMemo(() => {
    const missing = [];
    if (!branding?.address_line1) missing.push('Street address');
    if (!branding?.city) missing.push('City');
    if (!branding?.state) missing.push('State');
    if (!branding?.zip_code) missing.push('ZIP code');
    if (!branding?.phone) missing.push('Phone');
    return missing;
  }, [branding]);

  const today = new Date();
  const currentYear = today.getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  // ---- CivilX weekly pay-period selector state (Sunday–Saturday) ----
  const [payrollYear, setPayrollYear] = useState(currentYear);
  const [weeks, setWeeks] = useState([]);
  const [calMeta, setCalMeta] = useState({ current_year: null, current_week: null, existing: [], timezone: '' });
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekSearch, setWeekSearch] = useState('');
  const [calLoading, setCalLoading] = useState(false);
  const pendingWeekRef = useRef(null);
  const [calRefresh, setCalRefresh] = useState(0);

  // Payroll timezone picker + week preview + bulk generate
  const [tz, setTz] = useState(null);
  const [savingTz, setSavingTz] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [feeRule, setFeeRule] = useState({ mode: 'default', value: '', note: '' });
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchPayrolls();
    axios.get(`${API}/tax/reimbursements/pending-count`, { withCredentials: true })
      .then((r) => setReimb(r.data)).catch(() => {});
  }, []);

  // Load the calendar weeks whenever the form is open and the year changes.
  useEffect(() => {
    if (!showForm) return;
    let active = true;
    setCalLoading(true);
    axios.get(`${API}/payrolls/calendar-weeks`, { params: { year: payrollYear }, withCredentials: true })
      .then((r) => {
        if (!active) return;
        const d = r.data || {};
        const ws = d.weeks || [];
        setWeeks(ws);
        setCalMeta({ current_year: d.current_year, current_week: d.current_week, existing: d.existing || [], timezone: d.timezone });
        setTz(d.timezone);
        let target = pendingWeekRef.current;
        if (target === 'last') target = ws.length;
        pendingWeekRef.current = null;
        if (!target) target = (d.current_year === payrollYear) ? d.current_week : 1;
        setSelectedWeek(target || 1);
      })
      .catch(() => { if (active) setWeeks([]); })
      .finally(() => { if (active) setCalLoading(false); });
    return () => { active = false; };
  }, [showForm, payrollYear, calRefresh]);

  // Non-destructive week preview (driver/load/gross) for the selected week.
  useEffect(() => {
    if (!showForm || !selectedWeek) { setPreview(null); return; }
    let active = true;
    setPreviewLoading(true);
    const feeParams = feeRule.mode !== 'default' ? { fee_mode: feeRule.mode, fee_value: feeRule.mode === 'waived' ? 0 : Number(feeRule.value || 0) } : {};
    axios.get(`${API}/payrolls/week-preview`, { params: { year: payrollYear, week: selectedWeek, ...feeParams }, withCredentials: true })
      .then((r) => { if (active) setPreview(r.data); })
      .catch(() => { if (active) setPreview(null); })
      .finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; };
  }, [showForm, payrollYear, selectedWeek, calRefresh, feeRule.mode, feeRule.value]);

  const saveTz = async (newTz) => {
    setTz(newTz);
    setSavingTz(true);
    try {
      await axios.put(`${API}/payrolls/timezone`, { timezone: newTz }, { withCredentials: true });
      setCalRefresh((k) => k + 1); // recompute boundaries in the new tz
    } catch { /* keep prior tz on failure */ } finally {
      setSavingTz(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!window.confirm(
      `Generate payroll for every un-run week of ${payrollYear} that has delivered loads (up to the current week)? This may create several payroll runs.`
    )) return;
    setBulkRunning(true);
    setBulkResult(null);
    setError('');
    try {
      const r = await axios.post(`${API}/payrolls/generate-bulk`, { year: payrollYear }, { withCredentials: true });
      setBulkResult(r.data);
      fetchPayrolls();
      setCalRefresh((k) => k + 1);
    } catch (err) {
      setError('Bulk generate failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const selectedWeekObj = useMemo(
    () => weeks.find((w) => w.week_number === selectedWeek) || null,
    [weeks, selectedWeek]
  );
  const existingForWeek = useMemo(
    () => calMeta.existing.find((e) => e.week_number === selectedWeek) || null,
    [calMeta, selectedWeek]
  );
  const filteredWeeks = useMemo(() => {
    const q = weekSearch.trim().toLowerCase();
    if (!q) return weeks;
    return weeks.filter((w) => w.label.toLowerCase().includes(q));
  }, [weeks, weekSearch]);

  const atFirstOverall = payrollYear <= yearOptions[0] && (selectedWeek || 1) <= 1;
  const atLastOverall = payrollYear >= yearOptions[yearOptions.length - 1] && (selectedWeek || 0) >= weeks.length;

  const goPrevWeek = () => {
    if (atFirstOverall) return;
    if (selectedWeek > 1) setSelectedWeek(selectedWeek - 1);
    else { pendingWeekRef.current = 'last'; setPayrollYear((y) => y - 1); }
  };
  const goNextWeek = () => {
    if (atLastOverall) return;
    if (selectedWeek < weeks.length) setSelectedWeek(selectedWeek + 1);
    else { pendingWeekRef.current = 1; setPayrollYear((y) => y + 1); }
  };

  const fetchPayrolls = async () => {
    try {
      const res = await axios.get(`${API}/payrolls`, { withCredentials: true });
      setPayrolls(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateWeek = (flags = {}) => axios.post(
    `${API}/payrolls/generate`,
    { payroll_year: payrollYear, payroll_week_number: selectedWeek, admin_fee_override: feeRuleToBody(feeRule), ...flags },
    { withCredentials: true }
  );

  const confirmGate = (detail) => {
    if (detail.code === 'estimates_required') {
      const list = (detail.estimated_loads || []).slice(0, 8).map((l) => `• ${l.load_ref || l.load_id} — ${l.driver_name} (${l.distance || '?'} mi → est. $${Number(l.estimated_revenue || 0).toFixed(2)})`).join('\n');
      return window.confirm(`${detail.message}\n\n${list}\n\nFinalize with ESTIMATED amounts? They will be flagged "(est.)" on the pay statements.`) ? { allow_estimates: true } : null;
    }
    if (detail.code === 'lease_on_confirmation_required') {
      const list = (detail.owner_operators || []).slice(0, 8).map((o) => `• ${o.name} — ${o.pay_type || ''} ${o.pay_rate ?? ''}${o.pay_type === 'percentage' ? '%' : ''}: ${String(o.reason || '').replace(/_/g, ' ')}`).join('\n');
      return window.confirm(`${detail.message}\n\n${list}\n\nConfirm: their existing configuration already covers the ${detail.owner_operators?.[0]?.lease_percent ?? ''}% lease-on fee. It will be shown on the statement but NOT deducted again.`) ? { confirm_lease_on_ambiguity: true } : null;
    }
    return null;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedWeekObj) { setError('Select a pay period first.'); return; }
    const ws = selectedWeekObj.start_date;
    const we = selectedWeekObj.end_date;
    // Team-driver reminder: warn if any delivered team load this week has no
    // matching Pay Transfer yet (co-driver would go unpaid).
    try {
      const cov = await axios.get(`${API}/pay-transfers/coverage`, {
        params: { week_start: ws, week_end: we },
        withCredentials: true,
      });
      const unc = cov.data?.uncovered || [];
      if (unc.length) {
        const lines = unc
          .map((u) => `• ${u.from_name} → ${u.to_name} (${u.load_count} load${u.load_count === 1 ? '' : 's'})`)
          .join('\n');
        const proceed = window.confirm(
          `${unc.length} team-load driver pair${unc.length === 1 ? '' : 's'} have NO Pay Transfer for this week:\n\n${lines}\n\nThe co-driver(s) won't be paid unless you add a transfer above. Generate payroll anyway?`
        );
        if (!proceed) return;
      }
    } catch { /* coverage check is best-effort — never block generation */ }
    setGenerating(true);
    try {
      let flags = {};
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await generateWeek(flags);
          break;
        } catch (err) {
          const detail = err.response?.data?.detail;
          const extra = err.response?.status === 409 && detail && typeof detail === 'object' ? confirmGate(detail) : null;
          if (!extra) throw err;
          flags = { ...flags, ...extra };
        }
      }
      setShowForm(false);
      fetchPayrolls();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && ['estimates_required', 'lease_on_confirmation_required'].includes(detail.code)) {
        setError('Payroll not finalized — resolve the flagged items (load rates / lease-on method) or confirm to proceed.');
      } else if (err.response?.status === 409 && detail && typeof detail === 'object') {
        // Duplicate — surface View / Regenerate options inline.
        setCalMeta((m) => ({
          ...m,
          existing: [
            ...m.existing.filter((x) => x.week_number !== selectedWeek),
            { week_number: selectedWeek, payroll_id: detail.payroll_id, status: detail.status },
          ],
        }));
        setError('');
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to generate payroll');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (payrollId) => {
    if (!window.confirm(
      'Regenerate this payroll from current completed loads?\n\nDrafts are rebuilt in place. A payroll that was already SENT is kept as-is for drivers — the rebuilt figures become a pending revision you must review and send explicitly. Advance repayments and driver balances for this run are reversed and re-applied. No emails are sent now.'
    )) return;
    setGenerating(true);
    setError('');
    try {
      await axios.post(`${API}/payrolls/${payrollId}/recalculate`, {}, { withCredentials: true });
      setShowForm(false);
      fetchPayrolls();
    } catch (err) {
      setError('Failed to regenerate payroll');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payroll run? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/payrolls/${id}`, { withCredentials: true });
      fetchPayrolls();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to delete');
    }
  };

  const handleToggleLock = async (p) => {
    const locking = !p.locked;
    if (!window.confirm(
      locking
        ? 'Lock this pay week? Its loads and paystubs will be frozen — no edits, re-dating, deletes or regeneration until you unlock it.'
        : 'Unlock this pay week? Its loads and paystubs become editable again.'
    )) return;
    try {
      await axios.post(`${API}/payrolls/${p.id}/${locking ? 'lock' : 'unlock'}`, {}, { withCredentials: true });
      fetchPayrolls();
    } catch (err) {
      alert('Failed to update lock');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8">
        <button
          data-testid="back-to-dashboard-button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to Dashboard
        </button>

        <PayeeFeesPanel />

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Payroll</h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Weekly payroll generation from delivered loads. Net pay = total gross earnings − applicable deductions and fees + signed adjustments.</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                data-testid="bulk-generate-button"
                onClick={handleBulkGenerate}
                disabled={bulkRunning}
                title={`Generate all un-run weeks of ${payrollYear}`}
                className="flex items-center gap-2 bg-white border border-[#002FA7] text-[#002FA7] px-4 py-2 rounded-md font-semibold hover:bg-[#002FA7]/5 transition-all duration-200 disabled:opacity-50"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Layers className="w-4 h-4" strokeWidth={2} />
                {bulkRunning ? 'Generating…' : `Catch up ${payrollYear}`}
              </button>
            )}
            <button
              data-testid="generate-payroll-button"
              onClick={() => { track('click', 'payroll-generate-open'); setShowForm(!showForm); setError(''); setBulkResult(null); }}
              className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Generate Payroll
            </button>
          </div>
        </div>

        <PayrollAttentionBanner isAdmin={isAdmin} refreshKey={payrolls} />

        {bulkResult && (
          <div data-testid="bulk-generate-result" className={`mb-6 flex items-start gap-3 rounded-md p-3 sm:p-4 border ${bulkResult.failed_count ? 'bg-amber-50 border-amber-200' : 'bg-[#002FA7]/5 border-[#002FA7]/20'}`}>
            <Layers className={`w-5 h-5 shrink-0 mt-0.5 ${bulkResult.failed_count ? 'text-amber-600' : 'text-[#002FA7]'}`} strokeWidth={2} />
            <div className="flex-1 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <p className="font-semibold text-slate-900">Catch-up for {bulkResult.year} complete.</p>
              <p className="text-xs mt-0.5">
                Generated {bulkResult.generated_count} week{bulkResult.generated_count === 1 ? '' : 's'}
                {bulkResult.generated?.length ? ` (${bulkResult.generated.join(', ')})` : ''} ·
                {' '}skipped {bulkResult.skipped_existing.length} already-run ·
                {' '}skipped {bulkResult.skipped_empty.length} with no delivered loads.
              </p>
              {bulkResult.failed_count > 0 && (
                <div data-testid="bulk-generate-failed" className="mt-2 text-xs text-amber-800">
                  <p className="font-semibold">⚠ {bulkResult.failed_count} week{bulkResult.failed_count === 1 ? '' : 's'} failed and were skipped:</p>
                  <ul className="mt-1 space-y-0.5">
                    {bulkResult.failed.map((f) => (
                      <li key={f.week_number}>• Week {f.week_number} — {f.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button data-testid="dismiss-bulk-result" onClick={() => setBulkResult(null)} className="text-slate-400 hover:text-slate-600 text-xs font-semibold">Dismiss</button>
          </div>
        )}

        {reimb && reimb.count > 0 && (
          <div data-testid="payroll-reimbursement-badge" className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-[#002FA7]/5 border border-[#002FA7]/20 rounded-md p-3 sm:p-4">
            <DollarSign className="w-5 h-5 text-[#002FA7] shrink-0" strokeWidth={2} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {reimb.count} receipt{reimb.count !== 1 ? 's' : ''} pending reimbursement · ${Number(reimb.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Across {reimb.driver_count} driver{reimb.driver_count !== 1 ? 's' : ''}. Fold these into a payroll run before finalizing.
              </p>
            </div>
            <Link data-testid="payroll-reimbursement-link" to="/taxes/reimburse" className="text-sm font-semibold text-[#002FA7] hover:text-[#00247D] whitespace-nowrap flex items-center gap-1">
              Review reimbursements <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {missingBusinessFields.length > 0 && (
          <div
            data-testid="payroll-business-details-banner"
            className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-md p-3 sm:p-4"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Your business details are incomplete.
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Every paystub PDF header pulls these from Company Branding. Missing: {missingBusinessFields.join(', ')}.
              </p>
            </div>
            <Link
              to="/company/branding"
              data-testid="payroll-banner-finish-branding"
              className={`self-start sm:self-auto text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-2 whitespace-nowrap ${isAdmin ? '' : 'hidden'}`}
            >
              Finish business details
            </Link>
          </div>
        )}

        {showForm && (
          <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>New Payroll Run</h2>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              CivilX pay weeks run <span className="font-semibold text-slate-700">Sunday → Saturday</span>
              {calMeta.timezone ? <> in <span className="font-semibold text-slate-700">{calMeta.timezone}</span></> : null}. Week 1 contains January 1.
            </p>

            {/* Payroll timezone picker — makes Sunday–Saturday boundaries unmistakable */}
            {isAdmin && (
              <div data-testid="payroll-timezone-row" className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Globe className="w-4 h-4 text-[#002FA7]" /> Company Timezone
                </div>
                <select
                  data-testid="payroll-timezone-select"
                  value={tz || ''}
                  onChange={(e) => saveTz(e.target.value)}
                  disabled={savingTz}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {tz && !TZ_OPTIONS.includes(tz) && <option value={tz}>{tz.replace(/_/g, ' ')}</option>}
                  {TZ_OPTIONS.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
                </select>
                <span className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {savingTz ? 'Saving…' : 'Company profile timezone — payroll weeks, reports and tracking all use it. Only company admins can change it.'}
                </span>
              </div>
            )}
            {error && (
              <div data-testid="payroll-error-message" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{error}</div>
            )}

            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Payroll Year */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Payroll Year</label>
                  <select
                    data-testid="payroll-year-select"
                    value={payrollYear}
                    onChange={(e) => { pendingWeekRef.current = 1; setPayrollYear(Number(e.target.value)); setWeekSearch(''); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}{y === calMeta.current_year ? ' (current)' : ''}</option>
                    ))}
                  </select>
                </div>

                {/* Pay Period week */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pay Period</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="prev-week-button"
                      onClick={goPrevWeek}
                      disabled={atFirstOverall || calLoading}
                      aria-label="Previous week"
                      className="min-w-[40px] h-[42px] flex items-center justify-center border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <select
                      data-testid="pay-period-select"
                      value={selectedWeek || ''}
                      onChange={(e) => setSelectedWeek(Number(e.target.value))}
                      disabled={calLoading || weeks.length === 0}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {calLoading && <option>Loading…</option>}
                      {!calLoading && filteredWeeks.map((w) => {
                        const has = calMeta.existing.some((e) => e.week_number === w.week_number);
                        const isCur = calMeta.current_year === payrollYear && calMeta.current_week === w.week_number;
                        return (
                          <option key={w.week_number} value={w.week_number}>
                            {w.label}{isCur ? '  • current' : ''}{has ? '  ✓ generated' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      data-testid="next-week-button"
                      onClick={goNextWeek}
                      disabled={atLastOverall || calLoading}
                      aria-label="Next week"
                      className="min-w-[40px] h-[42px] flex items-center justify-center border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Search within weeks */}
                  <div className="relative mt-2">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      data-testid="pay-period-search"
                      value={weekSearch}
                      onChange={(e) => setWeekSearch(e.target.value)}
                      placeholder="Search weeks (e.g. Jan, Week 5)…"
                      className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    />
                  </div>
                </div>
              </div>

              {/* Selected period summary + live preview */}
              {selectedWeekObj && (
                <div data-testid="selected-period-summary" className="bg-slate-50 border border-slate-200 rounded-md px-3 py-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <Calendar className="w-4 h-4 text-[#002FA7]" />
                    <span>Pay period: <span className="font-semibold">{selectedWeekObj.start_date}</span> → <span className="font-semibold">{selectedWeekObj.end_date}</span></span>
                  </div>
                  <div data-testid="week-preview" className="grid grid-cols-3 gap-2 mt-3">
                    <div className="bg-white border border-slate-200 rounded-md px-3 py-2">
                      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-400"><Users className="w-3.5 h-3.5" /> Drivers</div>
                      <p data-testid="preview-driver-count" className="text-lg font-bold text-slate-900 mt-0.5">
                        {previewLoading ? '…' : (preview ? `${preview.drivers_with_loads}/${preview.active_driver_count}` : '—')}
                      </p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-md px-3 py-2">
                      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-400"><Truck className="w-3.5 h-3.5" /> Loads</div>
                      <p data-testid="preview-load-count" className="text-lg font-bold text-slate-900 mt-0.5">
                        {previewLoading ? '…' : (preview ? preview.load_count : '—')}
                      </p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-md px-3 py-2">
                      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-400"><DollarSign className="w-3.5 h-3.5" /> Est. Gross</div>
                      <p data-testid="preview-est-gross" className="text-lg font-bold text-green-700 mt-0.5">
                        {previewLoading ? '…' : (preview ? `$${Number(preview.estimated_gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—')}
                      </p>
                    </div>
                  </div>
                  {preview && !preview.load_count && (
                    <p className="text-xs text-amber-700 mt-2">No delivered loads in this period yet — generating will create an empty payroll.</p>
                  )}
                  <div data-testid="generate-admin-fee-section" className="mt-3 rounded-md border border-violet-200 bg-violet-50 px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-violet-700 font-semibold">Administration fee for this run</p>
                        <p data-testid="generate-admin-fee-amount" className="text-lg font-bold text-violet-900 mt-0.5" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                          {previewLoading ? '…' : (preview?.admin_fee ? `${feeMoney(preview.admin_fee.estimated_total)} total · ${preview.admin_fee.payee_count} payee${preview.admin_fee.payee_count === 1 ? '' : 's'}` : '—')}
                        </p>
                        <p className="text-xs text-violet-800" data-testid="generate-admin-fee-rule">
                          {feeRule.mode === 'default'
                            ? (preview?.admin_fee ? `Company default: ${describeFeeRule(preview.admin_fee.company_default_type, preview.admin_fee.company_default)} (per-payee overrides and waivers still apply)` : 'Company default applies')
                            : `This run: ${describeOverride({ ...feeRule, value: Number(feeRule.value || 0) }) || 'default'} — only authorized payees are charged; standing waivers still apply`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <FeeRulePicker value={feeRule} onChange={setFeeRule} idPrefix="generate-fee" defaultLabel="Use company default" />
                    </div>
                  </div>
                </div>
              )}

              {existingForWeek ? (
                <div data-testid="duplicate-payroll-warning" className="flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                  <p className="flex-1 text-sm text-amber-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    A payroll already exists for {payrollYear} · Week {selectedWeek}. You can view it or regenerate it from current loads.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="view-existing-payroll-button"
                      onClick={() => navigate(`/payroll/${existingForWeek.payroll_id}`)}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-[#002FA7] text-white rounded-md px-3 py-2 hover:bg-[#00247D]"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> View Payroll
                    </button>
                    <button
                      type="button"
                      data-testid="regenerate-payroll-button"
                      onClick={() => handleRegenerate(existingForWeek.payroll_id)}
                      disabled={generating}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-white border border-amber-300 text-amber-800 rounded-md px-3 py-2 hover:bg-amber-100 disabled:opacity-50"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> {generating ? 'Working…' : 'Regenerate'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="submit"
                  data-testid="submit-payroll-button"
                  disabled={generating || !selectedWeekObj}
                  className="bg-[#002FA7] text-white px-6 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {generating ? 'Generating...' : 'Generate'}
                </button>
              )}
            </form>
            {isAdmin && selectedWeekObj && (
              <PayTransfersPanel weekStart={selectedWeekObj.start_date} weekEnd={selectedWeekObj.end_date} />
            )}
          </div>
        )}

        {isAdmin && <StaffSalaryPanel />}

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Work Sans, sans-serif' }}>Payroll History</h2>

          {payrolls.length === 0 ? (
            <div className="text-center py-10">
              <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No payroll runs yet. Generate one to get started.</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3" data-testid="payroll-mobile-list">
                {payrolls.map((p) => (
                  <div key={p.id} data-testid={`payroll-card-${p.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                      <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        {p.week_start} → {p.week_end}{p.is_supplemental && <span data-testid={`supplemental-badge-${p.id}`} className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">Supplemental</span>}
                      </p>
                      {p.locked && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
                          <Lock className="w-3 h-3" /> Locked
                        </span>
                      )}
                      <PayrollStatusBadge payroll={p} compact />
                    </div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-xs mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span><span className="text-slate-400">Drivers:</span> <span className="text-slate-900 font-medium">{p.driver_count}</span></span>
                      <span><span className="text-slate-400">Loads:</span> <span className="text-slate-900 font-medium">{p.load_count}</span><ExcludedLoadsBadge payroll={p} /></span>
                      <span><span className="text-slate-400">Gross:</span> <span className="text-slate-900 font-semibold">${Number(p.total_gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                      <span><span className="text-slate-400">Net:</span> <span className="text-green-700 font-semibold">${Number(p.total_net || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                      <span className="col-span-2 text-[11px] text-slate-500">Generated {new Date(p.generated_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`mobile-view-payroll-${p.id}`}
                        onClick={() => navigate(`/payroll/${p.id}`)}
                        className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold text-[#002FA7] bg-[#002FA7]/5 active:bg-[#002FA7]/10 rounded-md"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <ExternalLink className="w-4 h-4" /> View
                      </button>
                      {isAdmin && (
                        <button
                          data-testid={`mobile-lock-payroll-${p.id}`}
                          onClick={() => handleToggleLock(p)}
                          className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md ${p.locked ? 'text-amber-700 bg-amber-50' : 'text-slate-500 bg-slate-50'}`}
                          aria-label={p.locked ? 'Unlock payroll' : 'Lock payroll'}
                        >
                          {p.locked ? <LockOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </button>
                      )}
                      <button
                        data-testid={`mobile-delete-payroll-${p.id}`}
                        onClick={() => handleDelete(p.id)}
                        disabled={p.locked}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center text-red-600 active:bg-red-50 rounded-md disabled:opacity-30"
                        aria-label="Delete payroll"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Week</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Drivers</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loads</th>
                    <th className="text-right py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Gross</th>
                    <th className="text-right py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Net</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Generated</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {payrolls.map((p) => (
                    <tr key={p.id} data-testid={`payroll-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#002FA7]" />
                          <span>{p.week_start} → {p.week_end}{p.is_supplemental && <span data-testid={`supplemental-badge-${p.id}`} className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">Supplemental</span>}</span>
                          {p.locked && (
                            <span data-testid={`payroll-lock-badge-${p.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
                              <Lock className="w-3 h-3" /> Locked
                            </span>
                          )}
                          <PayrollStatusBadge payroll={p} compact />
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{p.driver_count}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{p.load_count}<ExcludedLoadsBadge payroll={p} /></td>
                      <td data-testid={`payroll-gross-${p.id}`} className="py-3 px-2 text-sm text-slate-900 font-semibold text-right" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>${Number(p.total_gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td data-testid={`payroll-net-${p.id}`} className="py-3 px-2 text-sm text-green-700 font-semibold text-right" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>${Number(p.total_net || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-3 px-2 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{new Date(p.generated_at).toLocaleString()}</td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <button
                            data-testid={`view-payroll-${p.id}`}
                            onClick={() => navigate(`/payroll/${p.id}`)}
                            className="flex items-center gap-1 text-xs text-[#002FA7] hover:text-[#00247D] font-semibold"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </button>
                          {isAdmin && (
                            <button
                              data-testid={`lock-payroll-${p.id}`}
                              onClick={() => handleToggleLock(p)}
                              className={`flex items-center gap-1 text-xs font-semibold ${p.locked ? 'text-amber-700 hover:text-amber-900' : 'text-slate-500 hover:text-slate-800'}`}
                              title={p.locked ? 'Unlock pay week' : 'Lock pay week'}
                            >
                              {p.locked ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                              {p.locked ? 'Unlock' : 'Lock'}
                            </button>
                          )}
                          <button
                            data-testid={`delete-payroll-${p.id}`}
                            onClick={() => handleDelete(p.id)}
                            disabled={p.locked}
                            title={p.locked ? 'Unlock to delete' : 'Delete payroll'}
                            className="text-red-600 hover:text-red-800 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Payroll;
